import React from 'react';
import { motion } from 'framer-motion';
import { FaBriefcase, FaCalendarAlt, FaUsers } from 'react-icons/fa';
import AuroraBackground from './AuroraBackground';
import ParticleBackground from './ParticleBackground';
import portfolioData from '../data/portfolio.json';
import './Experience.css';

const Experience = () => {
    const experience = portfolioData.experience;
    const positions = portfolioData.positions;

    return (
        <div className="experience-page">
            <AuroraBackground />
            <ParticleBackground particleCount={25} />
            {/* WORK EXPERIENCE SECTION */}
            <section className="experience-section">
                <div className="section-background experience-bg"></div>
                <div className="container">
                    <motion.div
                        className="section-header"
                        initial={{ opacity: 0, y: -30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8 }}
                    >
                        <div className="section-icon-wrapper">
                            <FaBriefcase className="section-icon" />
                        </div>
                        <h1 className="section-main-title">Professional Experience</h1>
                        <p className="section-subtitle">A journey through my career milestones</p>
                        <div className="title-underline"></div>
                    </motion.div>

                    <div className="experience-timeline-wrapper">
                        <div className="timeline experience-timeline">
                            {experience.map((job, index) => (
                                <motion.div
                                    key={job.id}
                                    className={`timeline-item ${index % 2 === 0 ? 'left' : 'right'}`}
                                    initial={{ opacity: 0, y: 50, scale: 0.9 }}
                                    whileInView={{ opacity: 1, y: 0, scale: 1 }}
                                    viewport={{ once: true, margin: "-100px" }}
                                    transition={{
                                        duration: 0.6,
                                        delay: index * 0.15,
                                        type: "spring",
                                        stiffness: 100
                                    }}
                                    whileHover={{ scale: 1.02, y: -5 }}
                                >
                                    <div className="timeline-connector"></div>
                                    <div className="timeline-dot">
                                        <div className="dot-pulse"></div>
                                        <FaBriefcase className="dot-icon" />
                                    </div>
                                    <motion.div
                                        className="timeline-card"
                                        whileHover={{ boxShadow: "0 20px 40px rgba(0, 0, 0, 0.15)" }}
                                    >
                                        <div className="card-glow"></div>
                                        <div className="card-content">
                                            <div className="card-header">
                                                <h3 className="card-title">{job.position}</h3>
                                                <div className="card-badge experience-badge">Experience</div>
                                            </div>
                                            <h4 className="card-company">{job.company}</h4>
                                            <div className="card-duration">
                                                <FaCalendarAlt className="duration-icon" />
                                                <span>{job.duration}</span>
                                            </div>
                                            <p className="card-description">{job.description}</p>
                                            {job.technologies && job.technologies.length > 0 && (
                                                <div className="tech-stack">
                                                    {job.technologies.map((tech, techIndex) => (
                                                        <motion.span
                                                            key={techIndex}
                                                            className="tech-badge"
                                                            initial={{ opacity: 0, scale: 0 }}
                                                            whileInView={{ opacity: 1, scale: 1 }}
                                                            viewport={{ once: true }}
                                                            transition={{ delay: techIndex * 0.05 }}
                                                            whileHover={{ scale: 1.1 }}
                                                        >
                                                            {tech}
                                                        </motion.span>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </motion.div>
                                </motion.div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* POSITIONS OF RESPONSIBILITIES SECTION */}
            <section className="experience-section positions-section">
                <div className="section-background positions-bg"></div>
                <div className="container">
                    <motion.div
                        className="section-header"
                        initial={{ opacity: 0, y: -30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8 }}
                    >
                        <div className="section-icon-wrapper positions-icon-wrapper">
                            <FaUsers className="section-icon" />
                        </div>
                        <h1 className="section-main-title">Positions of Responsibilities</h1>
                        <p className="section-subtitle">Leadership roles and organizational contributions</p>
                        <div className="title-underline positions-underline"></div>
                    </motion.div>

                    <div className="experience-timeline-wrapper">
                        <div className="timeline positions-timeline">
                            {positions.map((position, index) => (
                                <motion.div
                                    key={index}
                                    className={`timeline-item ${index % 2 === 0 ? 'left' : 'right'}`}
                                    initial={{ opacity: 0, y: 50, scale: 0.9 }}
                                    whileInView={{ opacity: 1, y: 0, scale: 1 }}
                                    viewport={{ once: true, margin: "-100px" }}
                                    transition={{
                                        duration: 0.6,
                                        delay: index * 0.15,
                                        type: "spring",
                                        stiffness: 100
                                    }}
                                    whileHover={{ scale: 1.02, y: -5 }}
                                >
                                    <div className="timeline-connector positions-connector"></div>
                                    <div className="timeline-dot positions-dot">
                                        <div className="dot-pulse"></div>
                                        <FaUsers className="dot-icon" />
                                    </div>
                                    <motion.div
                                        className="timeline-card positions-card"
                                        whileHover={{ boxShadow: "0 20px 40px rgba(0, 0, 0, 0.15)" }}
                                    >
                                        <div className="card-glow positions-glow"></div>
                                        <div className="card-content">
                                            <div className="card-header">
                                                <h3 className="card-title">{position.title}</h3>
                                                <div className="card-badge positions-badge">Leadership</div>
                                            </div>
                                            <h4 className="card-company">{position.organization}</h4>
                                            <div className="card-duration">
                                                <FaCalendarAlt className="duration-icon" />
                                                <span>{position.duration}</span>
                                            </div>
                                        </div>
                                    </motion.div>
                                </motion.div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

        </div>
    );
};

export default Experience;